import classnames from "classnames";
import React, { useContext } from "react";
import {
  RefType,
  RewardType,
  ValidationType,
  WorkExperienceType,
} from "../../types";
import { StoreContext } from "../context/Store";
import AddSelectBlock from "../parts/AddSelectBlock";
import Button from "../parts/Button";
import ElementsList from "../parts/ElementsList";
import Rewards from "../parts/Rewards";
import SearchSelectBlock from "../parts/SearchSelectBlock";
import Text from "../parts/Text";
import WorkExperience from "../parts/WorkExperience";
import { scrollTo, validateLength } from "../utils";

export interface ExperienceProps {
  scrollTo: RefType;
}

export interface ExperienceState {}

const rewardValidation: ValidationType<RewardType> = {
  name: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  company: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  description: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  date: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
};

const workExperienceValidation: ValidationType<WorkExperienceType> = {
  role: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  company: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  description: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  dateStart: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
  dateEnd: {
    valid: (value: string) => validateLength(value),
    required: true,
  },
};

export const Experience = React.forwardRef<HTMLDivElement, ExperienceProps>(
  (props, ref) => {
    const store = useContext(StoreContext);
    const {
      setTempExperience,
      addExperience,
      tempExperience,
      experience,
      toggleExperience,
      tempReward,
      setTempReward,
      rewardEditIndex,
      reward,
      tempWorkExperience,
      editReward,
      addReward,
      removeReward,
      setTempWorkExperience,
      workExperienceEditIndex,
      addWorkExperience,
      editWorkExperience,
      removeWorkExperience,
      workExperience,
    } = store;
    const goNext = () => {
      scrollTo(props.scrollTo);
    };
    const renderReward = (item: RewardType) => {
      return (
        <>
          <Text tag="h5" color="blue">
            {item.date}
          </Text>
          <Text tag="p" classes="headline">
            {item.name} |&nbsp;{item.company}
          </Text>
          <Text tag="p" classes="subheadline">
            {item.description}
          </Text>
        </>
      );
    };
    const renderWorkExperience = (item: WorkExperienceType) => {
      return (
        <>
          <Text tag="h5" color="blue">
            {item.dateStart} - {item.dateEnd}
          </Text>
          <Text tag="h5">{item.role}</Text>
          <Text tag="p" classes="headline">
            {item.description}
          </Text>
          <Text tag="p" classes="subheadline">
            {item.company}
          </Text>
        </>
      );
    };
    return (
      <section id="experience" className="mb-5" ref={ref}>
        <Text tag="h2" color="pink" classes="mb-5">
          Doświadczenie
        </Text>
        <Text tag="h4" classes="mb-5">
          Jeśli już masz jakieś doświadczenie zawodowe, wpisz je tu:
        </Text>
        <div
          className={classnames("mb-5", {
            blink: workExperienceEditIndex !== null,
          })}
        >
          <WorkExperience
            temp={tempWorkExperience}
            onChange={setTempWorkExperience}
            validation={workExperienceValidation}
          />
        </div>
        <div className="row mb-5 justify-content-end">
          <div className="col-auto">
            <Button
              onClick={addWorkExperience}
              classes="w-100 smaller-text pink-border mb-2"
            >
              {workExperienceEditIndex !== null
                ? "aktualizuj doświadczenie"
                : "dodaj doświadczenie"}
            </Button>
          </div>
        </div>
        {workExperience.length > 0 && (
          <>
            <Text tag="h4" classes="mb-5">
              To Twoje osiągnięcia, z których możesz być dumny.
            </Text>
            <ElementsList
              data={workExperience}
              renderElement={renderWorkExperience}
              onEdit={editWorkExperience}
              onRemove={removeWorkExperience}
              editIndex={workExperienceEditIndex}
            />
          </>
        )}
        <Text tag="p">Jeszcze go nie masz?</Text>
        <Text tag="h4" classes="mb-5" color="blue">
          Na pewno jest coś, czym możesz się pochwalić:
        </Text>
        <SearchSelectBlock
          search
          data={experience}
          toggleAction={toggleExperience}
        >
          <AddSelectBlock
            value={tempExperience}
            add={addExperience}
            onChange={setTempExperience}
            placeholder="Inne (wpisz jakie)"
          />
        </SearchSelectBlock>
        <div className="d-flex justify-content-end mb-5">
          <Button
            onClick={addExperience}
            classes="smaller-text pink-border"
            disabled={tempExperience.length < 3}
          >
            dodaj
          </Button>
        </div>
        <Text tag="h4" classes="mb-5" color="blue">
          Wspomnij także o zdobytych nagrodach i wyróżnieniach:
        </Text>
        <div
          className={classnames("mb-5", { blink: rewardEditIndex !== null })}
        >
          <Rewards
            temp={tempReward}
            onChange={setTempReward}
            validation={rewardValidation}
          />
          {reward.length > 0 && (
            <>
              <Text tag="h4" classes="mb-5">
                To Twoje nagrody / wyróżnienia, z których możesz być dumny.
              </Text>
              <ElementsList
                data={reward}
                renderElement={renderReward}
                onEdit={editReward}
                onRemove={removeReward}
                editIndex={rewardEditIndex}
              />
            </>
          )}
        </div>

        <div className="row mb-5 justify-content-end">
          <div className="col-auto">
            <Button
              onClick={addReward}
              classes="w-100 smaller-text pink-border mb-2"
            >
              {rewardEditIndex !== null
                ? "aktualizuj wyróżnienie"
                : "dodaj wyróżnienie"}
            </Button>
          </div>
          <div className="col-auto">
            <Button onClick={goNext} classes="pink w-100 smaller-text mb-2">
              dalej
            </Button>
          </div>
        </div>
      </section>
    );
  }
);

export default Experience;
